import { PropsWithChildren } from 'react';

import { cn } from '@components/primitives/utilities';

import { EmptyStateIcon } from './EmptyStateIcon';
import { EmptyStateText } from './EmptyStateText';
import { EmptyStateTitle } from './EmptyStateTitle';

import css from './styles.module.css';

export interface EmptyStateProps extends PropsWithChildren {
  /** Applied to the root element, alongside the pattern's own classes. */
  className?: string;
  /** The scale to typeset and space at. Defaults to `md`. */
  size?: 'sm' | 'md';
  /**
   * The tone to render in. `failure` recolours the icon, title and text for
   * error states, such as a request that could not be loaded.
   */
  variant?: 'default' | 'failure';
}

/**
 * A centred placeholder for a region with nothing to show. Compose it from
 * `EmptyState.Icon`, `EmptyState.Title` and `EmptyState.Text`; any other
 * children are treated as actions and rendered below the text.
 *
 * @example
 * <EmptyState>
 *   <EmptyState.Icon><AppWindow /></EmptyState.Icon>
 *   <EmptyState.Title>No apps</EmptyState.Title>
 *   <EmptyState.Text>Create an app to get started.</EmptyState.Text>
 *   <Button>Create app</Button>
 * </EmptyState>
 */
export function EmptyState({
  children,
  className,
  size = 'md',
  variant = 'default',
}: EmptyStateProps) {
  return (
    <div
      className={cn(css.emptyState, className)}
      data-variant={variant}
      data-size={size}
    >
      {children}
    </div>
  );
}

EmptyState.Icon = EmptyStateIcon;
EmptyState.Title = EmptyStateTitle;
EmptyState.Text = EmptyStateText;

export type { EmptyStateTitleProps } from './EmptyStateTitle';
